import { useState, useEffect, useCallback } from 'react';
import { Phone, PhoneOff, Mic, MicOff, X, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import webrtcService from '../../services/webrtcService';

/**
 * AudioCall Component - Handles audio call UI
 */
const AudioCall = ({
    isOpen,
    onClose,
    callType, // 'outgoing', 'incoming', 'active'
    remoteUser,
    onCallEnd
}) => {
    const { t } = useTranslation();
    const [callState, setCallState] = useState(callType); // 'outgoing', 'incoming', 'active', 'ended'
    const [isMuted, setIsMuted] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [connectionState, setConnectionState] = useState('connecting');

    // Update call state when callType prop changes
    useEffect(() => {
        setCallState(callType);
    }, [callType]);

    // Call duration timer
    useEffect(() => {
        let interval;
        if (callState === 'active') {
            interval = setInterval(() => {
                setCallDuration(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [callState]);

    // Format duration as MM:SS
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Handle mute toggle
    const handleToggleMute = () => {
        const muted = webrtcService.toggleMute();
        setIsMuted(muted);
    };

    // Handle accept incoming call
    const handleAccept = useCallback(() => {
        webrtcService.acceptCall(webrtcService.currentCallId);
        setCallState('active');
    }, []);

    // Handle reject/decline call
    const handleReject = useCallback(() => {
        if (callState === 'incoming') {
            webrtcService.rejectCall(webrtcService.currentCallId);
        } else {
            webrtcService.endCall();
        }
        setCallState('ended');
        onCallEnd?.();
        onClose?.();
    }, [callState, onCallEnd, onClose]);

    // Handle end active call
    const handleEndCall = useCallback(() => {
        webrtcService.endCall();
        setCallState('ended');
        onCallEnd?.();
        onClose?.();
    }, [onCallEnd, onClose]);

    // Get initials
    const getInitials = (name) => {
        if (!name) return '?';
        const names = name.trim().split(' ');
        if (names.length >= 2) {
            return (names[0][0] + names[names.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Get status text
    const getStatusText = () => {
        switch (callState) {
            case 'outgoing':
                return t.call?.calling || 'Calling...';
            case 'incoming':
                return t.call?.incomingCall || 'Incoming call';
            case 'active':
                return formatDuration(callDuration);
            case 'ended':
                return t.call?.callEnded || 'Call ended';
            default:
                return '';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-3xl p-8 w-full max-w-sm mx-4 shadow-2xl">
                {/* Close button for non-active calls */}
                {callState !== 'active' && callState !== 'incoming' && (
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                )}

                {/* User avatar */}
                <div className="flex flex-col items-center mb-8">
                    <div className={`w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4 ${
                        callState === 'active'
                            ? 'bg-gradient-to-br from-green-500 to-green-600 animate-pulse'
                            : callState === 'incoming'
                            ? 'bg-gradient-to-br from-blue-500 to-blue-600 animate-bounce'
                            : 'bg-gradient-to-br from-linkoo-purple to-linkoo-pink'
                    }`}>
                        {getInitials(remoteUser?.name)}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-1">
                        {remoteUser?.name || t.call?.unknownCaller || 'Unknown'}
                    </h3>
                    <p className={`text-sm ${callState === 'active' ? 'text-green-400' : 'text-gray-400'}`}>
                        {getStatusText()}
                    </p>
                </div>

                {/* Call type indicator */}
                {(callState === 'outgoing' || callState === 'incoming') && (
                    <div className="flex justify-center mb-8">
                        {callState === 'outgoing' ? (
                            <PhoneOutgoing className="w-8 h-8 text-linkoo-purple animate-pulse" />
                        ) : (
                            <PhoneIncoming className="w-8 h-8 text-blue-400 animate-bounce" />
                        )}
                    </div>
                )}

                {/* Connection state indicator */}
                {callState === 'active' && connectionState !== 'connected' && (
                    <div className="text-center mb-4">
                        <span className="text-yellow-400 text-sm">
                            {t.call?.connecting || 'Connecting...'}
                        </span>
                    </div>
                )}

                {/* Call controls */}
                <div className="flex justify-center gap-6">
                    {/* Incoming call - Accept/Reject buttons */}
                    {callState === 'incoming' && (
                        <>
                            <button
                                onClick={handleReject}
                                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg"
                                title={t.call?.decline || 'Decline'}
                            >
                                <PhoneOff className="w-7 h-7 text-white" />
                            </button>
                            <button
                                onClick={handleAccept}
                                className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors shadow-lg animate-pulse"
                                title={t.call?.accept || 'Accept'}
                            >
                                <Phone className="w-7 h-7 text-white" />
                            </button>
                        </>
                    )}

                    {/* Outgoing call - Cancel button */}
                    {callState === 'outgoing' && (
                        <button
                            onClick={handleReject}
                            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg"
                            title={t.call?.cancel || 'Cancel'}
                        >
                            <PhoneOff className="w-7 h-7 text-white" />
                        </button>
                    )}

                    {/* Active call - Mute and End buttons */}
                    {callState === 'active' && (
                        <>
                            <button
                                onClick={handleToggleMute}
                                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors shadow-lg ${
                                    isMuted
                                        ? 'bg-red-500 hover:bg-red-600'
                                        : 'bg-gray-600 hover:bg-gray-500'
                                }`}
                                title={isMuted ? (t.call?.unmute || 'Unmute') : (t.call?.mute || 'Mute')}
                            >
                                {isMuted ? (
                                    <MicOff className="w-6 h-6 text-white" />
                                ) : (
                                    <Mic className="w-6 h-6 text-white" />
                                )}
                            </button>
                            <button
                                onClick={handleEndCall}
                                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg"
                                title={t.call?.endCall || 'End call'}
                            >
                                <PhoneOff className="w-7 h-7 text-white" />
                            </button>
                        </>
                    )}
                </div>

                {/* Audio quality indicator */}
                {callState === 'active' && (
                    <div className="mt-8 flex justify-center">
                        <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((bar) => (
                                <div
                                    key={bar}
                                    className={`w-1 rounded-full transition-all ${
                                        bar <= 4 ? 'bg-green-400' : 'bg-gray-600'
                                    }`}
                                    style={{ height: `${bar * 4 + 4}px` }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Hidden audio element for remote audio */}
            <audio id="remote-audio" autoPlay style={{ display: 'none' }} />
        </div>
    );
};

export default AudioCall;
